import { Outlet, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useQuery } from 'convex/react'
import { useConvexAuth } from 'convex/react'
import { api } from '@backend/_generated/api'
import type { Id } from '@backend/_generated/dataModel'
import { AppShell } from '@/components/common/app-shell'

export const Route = createFileRoute('/(dashboard)')({
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading } = useConvexAuth()
  const organizations = useQuery(
    api.profile.getMyOrganizations,
    isAuthenticated ? {} : 'skip',
  )
  const pendingInviteRedirect = sessionStorage.getItem('postAuthRedirectTo')
  const redirectInviteId =
    pendingInviteRedirect?.match(/^\/invites\/([^/]+)\/confirm$/)?.[1] ?? null
  const pendingInvite = useQuery(
    api.invites.getPublicInvite,
    redirectInviteId ? { inviteId: redirectInviteId as Id<'invites'> } : 'skip',
  )

  // OAuth providers may return directly to `/` without hitting our callback
  // route first. If we have a pending invite redirect, honor it before any
  // dashboard-level onboarding redirect logic can run.
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const pendingRedirect = sessionStorage.getItem('postAuthRedirectTo')
      const pendingMatch =
        pendingRedirect?.match(/^\/invites\/([^/]+)\/confirm$/) ?? null
      const isPendingInvitePath = !!pendingMatch
      const inviteStillPending =
        pendingInvite != null && pendingInvite.status === 'PENDING'

      if (pendingRedirect && isPendingInvitePath && inviteStillPending) {
        sessionStorage.removeItem('postAuthRedirectTo')
        window.location.href = pendingRedirect
      } else if (pendingRedirect && !inviteStillPending) {
        // Stale key from already accepted/expired invite; clear it to avoid loops.
        sessionStorage.removeItem('postAuthRedirectTo')
      }
    }
  }, [isLoading, isAuthenticated, pendingInvite])

  // Redirect users with no organizations to the onboarding flow.
  // organizations === undefined means still loading — wait before acting.
  useEffect(() => {
    const hasPendingInviteRedirect =
      sessionStorage.getItem('postAuthRedirectTo') != null &&
      pendingInvite != null &&
      pendingInvite.status === 'PENDING'

    if (
      !isLoading &&
      isAuthenticated &&
      !hasPendingInviteRedirect &&
      organizations !== undefined &&
      organizations.length === 0
    ) {
      void navigate({ to: '/onboarding', replace: true })
    }
  }, [isLoading, isAuthenticated, organizations, navigate])

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  )
}
